import z from 'zod';

// * As per https://docs.anthropic.com/en/docs/about-claude/models/overview, some models might not be available on Bedrock or vice-versa
export enum LLMModelId {
  Claude45Haiku = 'claude-4.5-haiku',
  Claude45Sonnet = 'claude-4.5-sonnet',
  Claude46Opus = 'claude-4.6-opus',
  Claude46Sonnet = 'claude-4.6-sonnet',
  Claude47Opus = 'claude-4.7-opus',
  Claude48Opus = 'claude-4.8-opus',
  Claude50Opus = 'claude-5.0-opus',
  Claude50Sonnet = 'claude-5.0-sonnet',
  Claude55Haiku = 'claude-5.5-haiku',
  Claude55Opus = 'claude-5.5-opus',
  Claude55Sonnet = 'claude-5.5-sonnet',
}

// * While the default is bedrock we want support for anthropic
export enum LLMModelType {
  Anthropic = 'anthropic',
  Bedrock = 'bedrock',
}

// * Selects how the structured-output answer field is described to the model
export enum LLMAnswerFormat {
  Email = 'email',
}

export const DEFAULT_VECTOR_MATCH_BUTTON_TEXT =
  'Klik hier voor meer informatie';

export const DEFAULT_LLM_MODEL_ID = LLMModelId.Claude55Sonnet; // * Current default as of October 16 2026
// * By default we want to run bedrock as of December 10th 2025
export const DEFAULT_LLM_MODEL_TYPE = LLMModelType.Bedrock;

const isLLMModelId = (modelId: unknown): modelId is LLMModelId => {
  return Object.values<unknown>(LLMModelId).includes(modelId);
};

// * Stored blocks can reference retired or unknown model ids, those run on the default model
export const resolveInvalidLLMModelId = (modelId: unknown): LLMModelId => {
  if (!isLLMModelId(modelId)) {
    return DEFAULT_LLM_MODEL_ID;
  }

  return modelId;
};

// * These models reject a forced tool call (tool_choice "any" or "tool") with a 400.
const FORCED_TOOL_CHOICE_UNSUPPORTED_MODEL_IDS: LLMModelId[] = [
  LLMModelId.Claude55Sonnet,
  LLMModelId.Claude55Opus,
];

export const supportsForcedToolChoice = (modelId: LLMModelId): boolean => {
  return !FORCED_TOOL_CHOICE_UNSUPPORTED_MODEL_IDS.includes(modelId);
};

// * These models think by default and reject thinking "disabled". Without
// * "between_tools" the thinking spends a small max_tokens budget and no text
// * block comes back.
const BETWEEN_TOOLS_THINKING_MODEL_IDS: LLMModelId[] = [
  LLMModelId.Claude55Sonnet,
];

export const getBedrockThinkingFields = (
  modelId: LLMModelId,
): { thinking: { type: 'between_tools' } } | undefined => {
  if (!BETWEEN_TOOLS_THINKING_MODEL_IDS.includes(modelId)) {
    return undefined;
  }

  return { thinking: { type: 'between_tools' } };
};

// * Models that only internal features call, such as the ticket classifier.
// * The flow designer does not offer them.
const INTERNAL_ONLY_MODEL_IDS: LLMModelId[] = [LLMModelId.Claude55Haiku];

const isSelectableModelId = (modelId: LLMModelId): boolean => {
  return !INTERNAL_ONLY_MODEL_IDS.includes(modelId);
};

// * The Anthropic type always forces the schema tool, so it only offers models that accept a forced tool call
export const getLLMModelIdsForType = (llmType?: string): LLMModelId[] => {
  const modelIds = Object.values(LLMModelId).filter(isSelectableModelId);

  if (llmType !== LLMModelType.Anthropic) {
    return modelIds;
  }

  return modelIds.filter(supportsForcedToolChoice);
};

// * The Anthropic type cannot run the platform default, because it forces a tool call
const ANTHROPIC_DEFAULT_LLM_MODEL_ID = LLMModelId.Claude46Sonnet;

export const getDefaultLLMModelIdForType = (llmType?: string): LLMModelId => {
  if (llmType === LLMModelType.Anthropic) {
    return ANTHROPIC_DEFAULT_LLM_MODEL_ID;
  }

  return DEFAULT_LLM_MODEL_ID;
};

export const LLMModelIdSchema = z.preprocess(
  resolveInvalidLLMModelId,
  z.enum(LLMModelId).default(DEFAULT_LLM_MODEL_ID),
);
export const DEFAULT_CLAUDE_TOKEN_LIMIT = 4096;

export enum LLMCostOperationName {
  PSN = 'PSN',
  HOT_TOPIC = 'hot-topic',
  SENTIMENT = 'sentiment',
  ARTICLE_ARCHITECT = 'article-architect',
  BRAIN_TEST = 'brain-test',
  MULTILINGUAL_TRANSLATION = 'multilingual-translation',
  EMAIL_AI_STUDIO = 'email-ai-studio',
  CONTACT_LOOKUP = 'contact-lookup',
  INTENT_CHECK = 'intent-check',
  PII_ANONYMIZATION = 'pii-anonymization',
  WEEKLY_BRIEF = 'weekly-brief',
  // * Tracked but deliberately not invoiced: the invoice script bills only a fixed
  // * list of operation names, and this name is not on it.
  ARTICLE_ARCHITECT_CLASSIFIER = 'article-architect-classifier',
}

export enum LLMCostCountType {
  WORDS = 'words',
  TOKENS = 'tokens',
}

export enum LLMCostTrigger {
  HOT_TOPIC_DAILY_SCHEDULER = 'hot-topic-daily-scheduler', // To track costs from daily scheduler runs for hot topic generation
  HOT_TOPIC_WEEKLY_SCHEDULER = 'hot-topic-weekly-scheduler', // To track costs from weekly scheduler runs for hot topic generation
  HOT_TOPIC_REGENERATE = 'hot-topic-regenerate', // When a super admin clicks regenerate button on the Hot Topics settings page
  HOT_TOPIC_WEEKLY_EXPORT = 'hot-topic-weekly-export', // To track costs from weekly export runs for hot topic generation
  HOT_TOPIC_REAL_TIME_CLASSIFICATION = 'hot-topic-real-time-classification', // Real-time hot-topic classifier that runs once per LLM block call
  SENTIMENT_REAL_TIME_CLASSIFICATION = 'sentiment-real-time-classification', // Real-time sentiment classifier that runs once per LLM block call
  ARTICLE_ARCHITECT_DOCUMENT_UPLOAD = 'article-architect-document-upload',
  ARTICLE_ARCHITECT_TRAIN_AI = 'article-architect-train-ai',
  ARTICLE_ARCHITECT_BCC_EMAIL = 'article-architect-bcc-email',
  ARTICLE_ARCHITECT_CONFLICT_MERGE = 'article-architect-conflict-merge',
  ARTICLE_ARCHITECT_CONFLICT_DETECTION = 'article-architect-conflict-detection',
  ARTICLE_ARCHITECT_CONFLICT_SCAN = 'article-architect-conflict-scan',
  ARTICLE_ARCHITECT_BACKFILL = 'article-architect-backfill', // TopDesk YTD/weekly backfill generation phase
  // * The TopDesk backfill ticket classifier stage. Tracked but deliberately not invoiced.
  ARTICLE_ARCHITECT_BACKFILL_CLASSIFIER = 'article-architect-backfill-classifier',
  BRAIN_TEST_EXPERIMENT = 'brain-test-experiment', // When a LangSmith experiment run completes
  EMAIL_AI_STUDIO_WEBHOOK = 'email-ai-studio-webhook',
  MULTILINGUAL_TRANSLATION = 'multilingual-translation', // When a reviewer triggers translation in the Train AI fact-checking modal
  CONTACT_LOOKUP_REAL_TIME = 'contact-lookup-real-time', // Real-time company name disambiguation in a ZvdZ chat flow
  INTENT_CHECK_REAL_TIME_CLASSIFICATION = 'intent-check-real-time-classification', // Real-time intent-check classifier that runs once per intent-check block call
  PII_ANONYMIZATION_REAL_TIME = 'pii-anonymization-real-time', // Real-time PII anonymization that runs once per LLM block call
  WEEKLY_BRIEF_SCHEDULER = 'weekly-brief-scheduler', // Monday Weekly Intelligence Brief generation (subject, analysis, recommendation)
  WEEKLY_BRIEF_SEND_TEST = 'weekly-brief-send-test', // A super admin sending themselves a test Weekly Intelligence Brief
  WEEKLY_BRIEF_MANUAL = 'weekly-brief-manual', // A super admin clicking Build now for a Weekly Intelligence Brief
}

export type TLLMUsageTranslation = {
  question: string;
  answer: string;
  sourceLang: string;
};

// * Pipeline stage emitted during a streaming LLM response.
// * Searching - vector search is running
// * Generating - docs ready, LLM call started
export enum LLMStreamStage {
  Searching = 'searching',
  Generating = 'generating',
}

// * Milliseconds the webclient typewriter takes to reveal one character during a
// * streaming LLM response (one character per tick). Shared so Core can size the
// * render-complete ack timeout to how long the widget animation can actually
// * take - keep this the single source of truth for both sides.
export const STREAM_TYPEWRITER_SPEED_MS = 25;
